/**
 * @file utils/Request/Ajax.js Ajax库封装
 * @author zhangzhe(zhangzhe@baidu.com)
 */

import _ from 'lodash';
import {toast} from 'acud';

import {isBrowser} from '../browserUtil';

const codeMessage = {
    400: '400：发出的请求有错误，服务器没有进行新建或修改数据的操作。',
    401: '401：用户没有权限（令牌、用户名、密码错误）。',
    403: '403：用户得到授权，但是访问是被禁止的。',
    404: '404：发出的请求针对的是不存在的记录，服务器没有进行操作。',
    406: '406：请求的格式不可得。',
    410: '410：请求的资源被永久删除，且不会再得到的。',
    422: '422：当创建一个对象时，发生一个验证错误。',
    500: '500：服务器发生错误，请检查服务器。',
    502: '502：网关错误。',
    503: '503：服务不可用，服务器暂时过载或维护。',
    504: '504：网关超时。'
};

// zhangzhe 2019-04-16 超时时长临时放宽到20分钟，要改回来
const DEFAULT_TIMEOUT = 60000 * 20;

// 默认请求头，等价于此前的 axios.defaults.headers.common
let defaultHeaders: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': 'application/json; charset=UTF-8'
};

export interface AjaxOption {
    silent?: boolean
    [propName: string]: any;
}

// 将 params 序列化为 query string，数组按 axios 默认行为展开为 key[]=a&key[]=b
function buildQuery(params?: any): string {
    if (_.isEmpty(params)) {
        return '';
    }
    const search = new URLSearchParams();
    _.each(params, (value, key) => {
        if (value === undefined || value === null) {
            return;
        }
        if (_.isArray(value)) {
            _.each(value, item => {
                if (item === undefined || item === null) {
                    return;
                }
                search.append(`${key}[]`, _.isObject(item) ? JSON.stringify(item) : String(item));
            });
        }
        else {
            search.append(key, _.isObject(value) ? JSON.stringify(value) : String(value));
        }
    });
    return search.toString();
}

export default class Ajax {

    setHeaders(headers: object) {
        defaultHeaders = {...defaultHeaders, ...headers} as Record<string, string>;
    }

    beforeSend(options?: AjaxOption) {
        return options;
    }

    beforeSuccess(data: any, options?: AjaxOption) {
        if (options || !options) {
            return data;
        }
    }

    // 基于 fetch 发起请求，处理 query、body、超时、credentials 等
    doFetch(url: string, {method = 'get', data, params, headers, timeout}: AjaxOption = {}) {
        const query = buildQuery(params);
        const fullUrl = query ? `${url}${url.includes('?') ? '&' : '?'}${query}` : url;

        const finalHeaders: Record<string, string> = {...defaultHeaders, ...headers};

        // get/head 请求不带 body，与老代码（get 用 params）逻辑一致
        let body: BodyInit | undefined;
        if (data !== undefined && data !== null && !['get', 'head'].includes(String(method).toLowerCase())) {
            if (typeof FormData !== 'undefined' && data instanceof FormData) {
                body = data;
                // FormData 交由浏览器设置带 boundary 的 Content-Type
                delete finalHeaders['Content-Type'];
            }
            else if (
                typeof data === 'string'
                || (typeof Blob !== 'undefined' && data instanceof Blob)
                || (typeof ArrayBuffer !== 'undefined' && data instanceof ArrayBuffer)
                || (typeof URLSearchParams !== 'undefined' && data instanceof URLSearchParams)
            ) {
                body = data;
            }
            else {
                body = JSON.stringify(data);
            }
        }

        const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
        const timer = controller
            ? setTimeout(() => controller.abort(), timeout || DEFAULT_TIMEOUT)
            : null;

        return fetch(fullUrl, {
            method: String(method).toUpperCase(),
            headers: finalHeaders,
            body,
            credentials: 'include',
            ...(controller ? {signal: controller.signal} : {})
        }).finally(() => {
            if (timer) {
                clearTimeout(timer);
            }
        });
    }

    request(url: string, options?: AjaxOption) {
        const opts
            = typeof this.beforeSend === 'function'
                ? this.beforeSend(options) || options
                : options;

        const responseType = opts?.responseType;

        return this.doFetch(url, opts)
            .then(async response => {
                const {status, statusText} = response;

                // 根据 responseType 解析响应体，等价于 axios 的 responseType 处理
                let responseData: any;
                if (responseType === 'blob') {
                    responseData = await response.blob();
                }
                else if (responseType === 'arraybuffer') {
                    responseData = await response.arrayBuffer();
                }
                else if (responseType === 'text') {
                    responseData = await response.text();
                }
                else {
                    // 默认按 json 解析，空 body 返回 null，非 JSON 文本原样返回
                    const text = await response.text();
                    if (!text) {
                        responseData = null;
                    }
                    else {
                        try {
                            responseData = JSON.parse(text);
                        }
                        catch (e) {
                            responseData = text;
                        }
                    }
                }

                if (status > 400 || status < 200) {
                    const error: any = new Error(
                        `API ${url} status is ${status} (${statusText})`
                    );
                    error.response = {status, statusText, data: responseData};
                    return Promise.reject(error);
                }
                return responseData;
            })
            .then(data => {
                if (typeof this.beforeSuccess === 'function') {
                    return this.beforeSuccess(data, options);
                }
                return data;
            })
            .catch(error => {
                if (error && error.message && isBrowser() && (!options || (options && !options.silent))) {
                    const errorCode = _.get(error, 'response.status');
                    const errorMessage = (errorCode && codeMessage[errorCode]) ? codeMessage[errorCode] : error.message;
                    toast.error({
                        message: errorMessage,
                        duration: 5
                    });
                }
                return Promise.reject(error);
            });
    }

    post(url: string, data?: any, options?: AjaxOption) {
        const opts = {
            method: 'post',
            data,
            ...options
        };
        return this.request(url, opts);
    }

    get(url: string, data?: any, options?: AjaxOption) {
        const opts = {
            method: 'get',
            params: data,
            ...options
        };
        return this.request(url, opts);
    }

    put(url: string, data?: any, options?: AjaxOption) {
        const opts = {
            method: 'put',
            data,
            ...options
        };
        return this.request(url, opts);
    }

    delete(url: string, data?: any, options?: AjaxOption) {
        const opts = {
            method: 'delete',
            data,
            ...options
        };
        return this.request(url, opts);
    }
}
